// Web service-worker registration. The Workbox SW (dist/sw.js) is built and
// deployed by `npm run web:sw`, but nothing registered it — so its precache,
// runtime caching (Firestore, images, OSM tiles), and navigateFallback never
// ran. This wires the registration into app startup and drives the
// reload-to-update prompt (see UpdateAvailableToast + workbox-config.js).

interface RegisterOptions {
  // Called when an updated SW has installed and is waiting to activate.
  onUpdate?: () => void;
}

let waitingWorker: ServiceWorker | null = null;
// Set only when the user accepts the update, so the controllerchange that
// follows skipWaiting triggers exactly one reload — and the initial
// clientsClaim on a first-ever visit does NOT reload the page.
let updateAccepted = false;
// Module-scoped so applyUpdate() can reload from the user-gesture stack and
// still suppress the controllerchange fallback (see applyUpdate).
let reloading = false;

export function registerServiceWorker(options: RegisterOptions = {}): void {
  // Dev (`expo start --web`) ships unhashed, fast-changing bundles and HMR;
  // a SW would serve stale assets and fight the dev server. Only the
  // production export (NODE_ENV=production) should register.
  if (process.env.NODE_ENV !== 'production') return;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  const notify = (worker: ServiceWorker) => {
    waitingWorker = worker;
    options.onUpdate?.();
  };

  const register = () => {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!updateAccepted || reloading) return;
      reloading = true;
      window.location.reload();
    });

    navigator.serviceWorker
      // 'none': update checks must bypass the HTTP cache for sw.js AND its
      // importScripts files (firebase-messaging-sw.js, sw-skip-waiting.js) —
      // the imports are unhashed, so a cached copy would pin old bytes.
      .register('/sw.js', { updateViaCache: 'none' })
      .then((registration) => {
        // An update may already be parked from a prior load.
        if (registration.waiting && navigator.serviceWorker.controller) {
          notify(registration.waiting);
        }
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            // installed + an existing controller => this is an update (not the
            // first install), so it's safe to prompt for a reload.
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              notify(installing);
            }
          });
        });
        // The browser only re-fetches sw.js on navigation or its ~24h check,
        // so a long-lived tab never learns about a deploy. Re-check whenever
        // the tab regains focus; this only feeds the prompt flow above and
        // never reloads by itself.
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update().catch(() => {});
          }
        });
      })
      .catch(() => {
        // Registration failures are non-fatal: the app works online without a
        // SW; offline caching just won't be available.
      });
  };

  // This runs from a React effect, which usually fires AFTER window's `load`
  // event — so attaching to `load` would miss it and never register. Register
  // now if the document is already loaded, otherwise wait for load.
  if (document.readyState === 'complete') {
    register();
  } else {
    window.addEventListener('load', register, { once: true });
  }
}

// Activate the waiting worker, then reload into the new version. Two
// constraints pull against each other here:
// - Reloading synchronously after postMessage races skipWaiting → activate;
//   when the reload wins, the fresh page is still controlled by the OLD
//   worker, finds the SAME worker parked in registration.waiting, and
//   re-shows the prompt — an infinite prompt→reload loop (every guard above
//   is module state that resets on reload).
// - Deferring the reload to the controllerchange callback breaks iOS Safari,
//   which ignores window.location.reload() without a user gesture on the
//   stack. That was the bug 76d7766 fixed.
// So: wait for the waiting worker's own statechange to reach 'activated'
// (direct signal, independent of clients.claim timing), capped at 2s so a
// wedged worker can't strand the user AND the reload stays inside Safari's
// transient-activation window (several seconds; activation is normally
// sub-second).
export function applyUpdate(): void {
  const worker = waitingWorker;
  if (!worker) return;
  updateAccepted = true;
  // applyUpdate owns the reload; suppress the controllerchange fallback.
  reloading = true;
  waitingWorker = null;

  let reloaded = false;
  const reload = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };

  worker.addEventListener('statechange', () => {
    if (worker.state === 'activated' || worker.state === 'redundant') reload();
  });
  worker.postMessage({ type: 'SKIP_WAITING' });
  setTimeout(reload, 2000);
}
